import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useTurnstile } from '../lib/useTurnstile';
import { AuthShell } from '../components/AuthShell';

const loginFormSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginFormSchema>;

const flashSchema = z.enum(['ok']).optional();

const loginSearchSchema = z.object({
  reset: flashSchema,
  activated: flashSchema,
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setToken = useAuthStore((s) => s.setToken);
  const search = Route.useSearch();
  const [serverError, setServerError] = useState<string | null>(null);
  const { ref: turnstileRef, token: turnstileToken, reset: resetTurnstile } =
    useTurnstile();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginFormSchema) });

  const onSubmit = async (values: LoginForm) => {
    setServerError(null);
    if (!turnstileToken) {
      setServerError('Please complete the bot check above.');
      return;
    }
    try {
      const result = await api.login({ ...values, turnstileToken });
      setToken(result.sessionToken);
      const me = await api.me();
      setUser(me);
      // `/` redirects by role to the user's landing page (see routes/index.tsx).
      void navigate({ to: '/' });
    } catch (err) {
      resetTurnstile();
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  const flashMessage =
    search.reset === 'ok'
      ? 'Password updated. Sign in with your new password.'
      : search.activated === 'ok'
        ? 'Account activated. Sign in with your new password.'
        : null;

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-bold tracking-tight">Sign in</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
        Staff use email and password. Parents and students can{' '}
        <Link to="/otp" className="text-gold-700 underline">
          sign in with a code
        </Link>
        .
      </p>

      {flashMessage && (
        <div
          role="status"
          className="mt-6 rounded-md border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-4 py-3 text-sm text-[color:var(--color-success)]"
        >
          {flashMessage}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
            aria-invalid={errors.email ? 'true' : undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-danger">
              {errors.email.message}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="password" className="block text-sm font-medium">
              Password
            </label>
            <Link to="/forgot" className="text-xs text-gold-700 underline">
              Forgot?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
            aria-invalid={errors.password ? 'true' : undefined}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          {errors.password && (
            <p id="password-error" className="mt-1 text-sm text-danger">
              {errors.password.message}
            </p>
          )}
        </div>
        <div ref={turnstileRef} role="group" aria-label="Bot check" />
        {serverError && (
          <p role="alert" className="text-sm text-danger">
            {serverError}
          </p>
        )}
        <button
          type="submit"
          disabled={isSubmitting || !turnstileToken}
          className="w-full inline-flex items-center justify-center rounded-md bg-ink-900 text-parchment-50 px-5 py-2.5 text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  );
}
